import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  UserCheck, 
  Clock, 
  X, 
  Plus, 
  AlertTriangle,
  Ghost,
  Move
} from 'lucide-react';
import { showAlert, showConfirm } from '../common/AppDialogs';

interface ImpersonationSession {
  id: string;
  impersonationId: string;
  sessionType: 'impersonation' | 'ghost_mode';
  targetUser: {
    id: string;
    name: string;
    email: string;
    organizationId: string;
  } | null;
  expiresAt: string;
  startedAt: string;
  remainingTime: number;
  isActive: boolean;
}

interface ImpersonationIndicatorProps {
  impersonationId?: string;
  ghostOrganizationId?: string;
  ghostSessionId?: string;
}

export default function ImpersonationIndicator({ impersonationId, ghostOrganizationId, ghostSessionId }: ImpersonationIndicatorProps) {
  const [session, setSession] = useState<ImpersonationSession | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [remainingTime, setRemainingTime] = useState(0);

  const activeId = impersonationId || ghostSessionId;
  const isGhostMode = !!ghostOrganizationId;

  // Fetch support session status from the backend for both impersonation and ghost mode.
  useEffect(() => {
    if (!activeId) return;

    const fetchSessionStatus = async () => {
      try {
        const response = await api.get(`/admin/impersonation/${activeId}/status`);
        if (response.data.success) {
          setSession(response.data.session);
          setRemainingTime(response.data.session.remainingTime);
        }
      } catch (error) {
        console.error('Error fetching session status:', error);
        // If session is not found, clear the indicator
        setSession(null);
      }
    };

    fetchSessionStatus();
    const interval = setInterval(fetchSessionStatus, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [activeId]);

  // Update remaining time countdown
  useEffect(() => {
    if (!session || !session.isActive) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const expiresAt = new Date(session.expiresAt).getTime();
      const remaining = Math.max(0, expiresAt - now);
      
      setRemainingTime(remaining);
      
      if (remaining <= 0) {
        setSession(null);
        // Redirect back to admin dashboard
        window.location.href = '/admin';
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  // Handle extending support session
  const handleExtendSession = async () => {
    if (!session) return;

    try {
      const response = await api.post(`/admin/impersonation/${session.impersonationId}/extend`);
      if (response.data.success) {
        setSession(prev => prev ? {
          ...prev,
          expiresAt: response.data.expiresAt,
          remainingTime: response.data.remainingTime
        } : null);
        setRemainingTime(response.data.remainingTime);
      }
    } catch (error: any) {
      console.error('Error extending session:', error);
      showAlert(`Failed to extend session: ${error.response?.data?.error || error.message}`);
    }
  };

  // Handle ending session
  const handleEndSession = async () => {
    if (!session) return;

    const confirmEnd = await showConfirm(
      isGhostMode 
        ? `Are you sure you want to exit Ghost Mode for organization "${ghostOrganizationId}"?`
        : `Are you sure you want to stop impersonating ${session.targetUser?.name}?`
    );

    if (!confirmEnd) return;

    try {
      await api.post(`/admin/impersonation/${session.impersonationId}/end`);

      sessionStorage.removeItem('nexus_impersonation');
      sessionStorage.removeItem('ghostModeSession');

      window.location.href = isGhostMode ? '/admin?tab=organizations' : '/admin?tab=users';
    } catch (error: any) {
      console.error('Error ending session:', error);
      showAlert(`Failed to end session: ${error.response?.data?.error || error.message}`);
    }
  };

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Format remaining time
  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Don't show indicator if no active session
  if (!session || !session.isActive) {
    return null;
  }

  return (
    <div
      className="fixed z-[70] cursor-move"
      style={{
        left: position.x,
        top: position.y,
        userSelect: isDragging ? 'none' : 'auto'
      }}
      onMouseDown={handleMouseDown}
    >
      <Card className={`shadow-2xl border-4 backdrop-blur-sm max-w-sm animate-pulse ring-4 ring-opacity-75 ${
        isGhostMode 
          ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-indigo-50 ring-purple-200'
          : 'border-red-500 bg-gradient-to-r from-red-50 to-orange-50 ring-red-200'
      }`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {isGhostMode ? (
                <Ghost className="h-4 w-4 text-purple-600" />
              ) : (
                <UserCheck className="h-4 w-4 text-orange-600" />
              )}
              <Badge variant="secondary" className={isGhostMode ? 'text-purple-600 bg-purple-50' : 'text-orange-600 bg-orange-50'}>
                {isGhostMode ? 'Ghost Mode' : 'Impersonating'}
              </Badge>
            </div>
            <Move className="h-3 w-3 text-gray-400" />
          </div>

          {!isGhostMode && session.targetUser && (
            <div className="mb-2">
              <div className="text-sm font-medium">{session.targetUser.name}</div>
              <div className="text-xs text-muted-foreground">{session.targetUser.email}</div>
              <div className="text-xs text-muted-foreground">Org: {session.targetUser.organizationId}</div>
            </div>
          )}

          {isGhostMode && (
            <div className="mb-2">
              <div className="text-sm font-medium">Ghost Mode: {ghostOrganizationId}</div>
              <div className="text-xs text-muted-foreground">Viewing organization with full admin permissions</div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3 text-gray-500" />
              <span className="text-xs text-gray-600">
                {formatTime(remainingTime)} remaining
              </span>
            </div>
            {remainingTime < 5 * 60 * 1000 && ( // Less than 5 minutes
              <AlertTriangle className="h-3 w-3 text-red-500" />
            )}
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExtendSession}
              className="flex-1 text-xs"
                          disabled={isGhostMode ? remainingTime >= 180 * 60 * 1000 : remainingTime >= 3 * 60 * 60 * 1000} // Ghost mode max 180min, impersonation max 3 hours
            title={isGhostMode ? "Extend ghost mode by 30 minutes (max 180 minutes total)" : "Extend impersonation session by 30 minutes"}
            >
              <Plus className="h-3 w-3 mr-1" />
              +30min
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleEndSession}
              className="flex-1 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Exit
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
